import { Component, input, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChatMessage } from '../types';
import { marked } from 'marked';

@Component({
  selector: 'app-chat-message',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './chat-message.component.html',
  styleUrl: './chat-message.component.scss',
})
export class ChatMessageComponent {
  message = input.required<ChatMessage>();
  isStreaming = input(false);

  private readonly translate = inject(TranslateService);

  protected renderedContent = '';
  private typewriterIndex = 0;
  private typewriterTimer: ReturnType<typeof setInterval> | null = null;
  private cachedRawContent = '';

  constructor() {
    effect(() => {
      const msg = this.message();
      const streaming = this.isStreaming();

      if (msg.role === 'assistant' && streaming) {
        if (msg.content !== this.cachedRawContent) {
          this.cachedRawContent = msg.content;
        }
        this.startTypewriter(msg.content);
      } else if (msg.role === 'assistant') {
        this.stopTypewriter();
        this.cachedRawContent = msg.content;
        this.renderedContent = marked.parse(this.stripThinkTags(msg.content || '')) as string;
      } else {
        this.stopTypewriter();
        this.renderedContent = msg.content;
      }
    });
  }

  private startTypewriter(fullContent: string): void {
    if (this.typewriterTimer) return;
    this.typewriterIndex = this.renderedContent.length > 0
      ? Math.min(this.renderedContent.length, fullContent.length) : 0;

    this.typewriterTimer = setInterval(() => {
      this.typewriterIndex += 3;
      if (this.typewriterIndex >= fullContent.length) {
        this.typewriterIndex = fullContent.length;
        this.stopTypewriter();
      }
      const display = fullContent.slice(0, this.typewriterIndex);
      this.renderedContent = marked.parse(this.stripThinkTags(display)) as string;
    }, 30);
  }

  /** 剥离模型思考链标签（<think>...</think>），避免展示给用户 */
  private stripThinkTags(content: string): string {
    if (!content || !content.includes('<think>')) return content;
    return content.replace(/<think>[\s\S]*?<\/think>/g, '')
      .replace(/^[\s\n]+/, '');
  }

  private stopTypewriter(): void {
    if (this.typewriterTimer) {
      clearInterval(this.typewriterTimer);
      this.typewriterTimer = null;
    }
  }

  protected copyContent(): void {
    navigator.clipboard.writeText(this.message().content);
  }

  protected get messageTime(): string {
    const d = new Date(this.message().timestamp);
    const locale = this.translate.currentLang === 'zh' ? 'zh-CN' : 'en-US';
    return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  }
}
